<template>
<v-container fluid class="bank-internal-page pa-3 pa-md-5">
  <div class="d-flex align-center mb-5">
    <v-icon color="primary" large class="ml-3">
      mdi-bank-transfer
    </v-icon>

    <div class="text-h5 font-weight-medium">
      چاپ عملیات واریز و برداشت ملت
    </div>
  </div>

  <v-form ref="form" v-model="valid" lazy-validation>
    <v-card outlined>
      <v-card-text class="pa-4 pa-md-5">
        <!-- اطلاعات مشترک -->
        <div class="section-title">
          <v-icon class="ml-2">mdi-card-account-details</v-icon>
          اطلاعات مشترک
        </div>

        <!-- سطر اول: تلفن، نشانی، شعبه و کد ملی -->
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="form.phone" label="تلفن ثابت" prepend-inner-icon="mdi-phone" maxlength="20" type="tel" underlined />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="form.address" label="نشانی" prepend-inner-icon="mdi-map-marker" underlined />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="form.branch" label="شعبه" prepend-inner-icon="mdi-bank" underlined :rules="requiredRules" />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="form.nationalCode" label="کد ملی" prepend-inner-icon="mdi-card-account-details-outline" underlined maxlength="10" />
          </v-col>
        </v-row>

        <!-- سطر دوم: تاریخ و مبلغ مشترک -->
        <v-row>
          <v-col cols="12" sm="6" md="2">
            <MyDate ref="dateField" v-model="form.date" label="تاریخ" :rules="requiredRules" underlined @setValue="form.date = $event" />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <div ref="amountField">
              <MyCurrency v-model="form.amount" label="مبلغ حواله (ریال)" :rules="requiredRules" underlined />
            </div>
          </v-col>

          <v-col v-if="normalizedAmount" cols="12" sm="12" md="8">
            <div class="amount-summary">
              <div>
                <span class="amount-label">مبلغ به عدد:</span>
                <span>{{ displayAmount }} تومان</span>
              </div>

              <div>
                <span class="amount-label">مبلغ به حروف:</span>
                <span>{{ displayAmountInWords }} تومان</span>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-5" />

        <!-- اطلاعات برداشت -->
        <div class="section-title">
          <v-icon class="ml-2">mdi-bank-minus</v-icon>
          اطلاعات برداشت
        </div>

        <v-row>
          <v-col cols="12" md="6">
            <MyBankaccounts ref="withdrawalAccountField" v-model="form.withdrawalAccountId" label="انتخاب حساب برداشت" :rules="requiredRules" @selected="onWithdrawalAccountSelected" />
          </v-col>
        </v-row>

        <v-divider class="my-5" />

        <!-- اطلاعات واریز -->
        <div class="section-title">
          <v-icon class="ml-2">mdi-bank-plus</v-icon>
          اطلاعات واریز
        </div>

        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="form.depositAccountNumber" label="شماره حساب/کارت ملت" prepend-inner-icon="mdi-credit-card-outline" underlined :rules="requiredRules" />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="form.depositOwner" label="نام صاحب حساب" prepend-inner-icon="mdi-account" underlined :rules="requiredRules" />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="form.depositorName" label="نام واریزکننده" prepend-inner-icon="mdi-account-arrow-left" underlined :rules="requiredRules" />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="form.depositReason" label="بابت" prepend-inner-icon="mdi-text-box-outline" underlined :rules="requiredRules" />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <!-- عملیات -->
      <v-card-actions class="pa-4 flex-wrap">
        <v-btn color="error" large :disabled="!canPrintWithdrawal" @click="printWithdrawal">
          <v-icon right>mdi-printer</v-icon>
          چاپ برداشت
        </v-btn>

        <v-btn color="success" large :disabled="!canPrintDeposit" @click="printDeposit">
          <v-icon right>mdi-printer</v-icon>
          چاپ واریز
        </v-btn>

        <v-spacer />

        <v-btn color="secondary" text large @click="resetForm">
          <v-icon right>mdi-refresh</v-icon>
          پاک کردن فرم
        </v-btn>

        <v-btn color="primary" text large @click="goToDashboard">
          <v-icon right>mdi-exit-to-app</v-icon>
          خروج
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</v-container>
</template>

<script>
import {
  numberToWords
} from '@persian-tools/persian-tools'

import MyBankaccounts from '@/components/MyBankaccounts.vue'
import MyCurrency from '@/components/MyCurrency.vue'
import MyDate from '@/components/MyDate.vue'

import {
  toJalaali
} from '@/functions/myDate'

const DEFAULT_PHONE = '۳۳۶۴۱۹۸۶'
const DEFAULT_ADDRESS = 'بازار رضا پلاک ۲/۶۰۵'

const COMMON_LAYOUT = {
  date: {
    left: 15,
    top: 14,
    width: 38,
    fontSize: 9,
    align: 'left',
  },
  branch: {
    left: 15,
    top: 20,
    width: 38,
    fontSize: 9,
    align: 'left',
  },
  nationalCode: {
    left: 24,
    top: 62,
    width: 46,
    fontSize: 9,
    align: 'left',
  },
  phone: {
    left: 152,
    top: 75,
    width: 30,
    fontSize: 9,
    align: 'left',
  },
  address: {
    left: 15,
    top: 70,
    width: 63,
    fontSize: 8,
    align: 'right',
  },
}

const WITHDRAWAL_LAYOUT = {
  amount: {
    left: 35,
    top: 30,
    width: 70,
    fontSize: 12,
    align: 'left',
  },

  amountInWords: {
    left: 20, //15
    top: 37,
    width: 75,
    fontSize: 7,
    align: 'right',
  },

  accountNumber: {
    left: 25,
    top: 43,
    width: 67,
    fontSize: 11,
    align: 'left',
  },

  owner: {
    left: 15,
    top: 50,
    width: 67,
    fontSize: 10,
    align: 'right',
  },
}

const DEPOSIT_LAYOUT = {
  amount: {
    left: 135,
    top: 30,
    width: 70,
    fontSize: 12,
    align: 'left',
  },

  amountInWords: {
    left: 120,
    top: 37,
    width: 75,
    fontSize: 7,
    align: 'right',
  },

  accountNumber: {
    left: 125,
    top: 43,
    width: 67,
    fontSize: 10,
    align: 'left',
  },

  owner: {
    left: 115,
    top: 50,
    width: 67,
    fontSize: 10,
    align: 'right',
  },

  depositorName: {
    left: 107,
    top: 62,
    width: 60,
    fontSize: 9,
    align: 'right',
  },

  reason: {
    left: 1,
    top: 75,
    width: 63,
    fontSize: 8,
    align: 'right',
  },
}

// const MM_TO_PX = 96 / 25.4

function toEnglishDigits(value) {
  return String(value == null ? '' : value)
    .replace(/[۰-۹]/g, digit =>
      String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))
    )
    .replace(/[٠-٩]/g, digit =>
      String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
    )
}

function toPersianDigits(value) {
  return toEnglishDigits(value).replace(
    /\d/g,
    digit => '۰۱۲۳۴۵۶۷۸۹' [Number(digit)]
  )
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function normalizeAmount(value) {
  return toEnglishDigits(value)
    .replace(/[^\d]/g, '')
    .replace(/^0+/, '')
}

function formatAmount(value) {
  if (!value) {
    return '۰'
  }

  return Number(value).toLocaleString('fa-IR')
}

function amountToWords(value) {
  if (!value) {
    return 'صفر'
  }

  return numberToWords(Number(value))
}

export default {
  name: 'BankPrintInternal',

  components: {
    MyBankaccounts,
    MyCurrency,
    MyDate,
  },

  data() {
    return {
      valid: false,

      requiredRules: [
        value =>
        !!String(value == null ? '' : value).trim() ||
        'این فیلد الزامی است',
      ],

      form: {
        date: '',
        branch: '',
        nationalCode: '',
        address: DEFAULT_ADDRESS,
        phone: DEFAULT_PHONE,

        amount: '',

        withdrawalAccountId: null,

        depositAccountNumber: '',
        depositOwner: '',
        depositorName: 'قاسمی',
        depositReason: 'خرید نقره',
      },

      withdrawalAccount: {},
    }
  },

  computed: {
    normalizedAmount() {
      return normalizeAmount(this.form.amount)
    },

    amountFormatted() {
      return formatAmount(this.normalizedAmount)
    },

    amountInWords() {
      return amountToWords(this.normalizedAmount)
    },

    displayAmount() {
      return formatAmount(Math.floor(Number(this.normalizedAmount) / 10))
    },

    displayAmountInWords() {
      return amountToWords(Math.floor(Number(this.normalizedAmount) / 10))
    },

    canPrintWithdrawal() {
      return Boolean(
        this.form.withdrawalAccountId &&
        this.normalizedAmount &&
        this.withdrawalAccount.number &&
        this.withdrawalAccount.owner
      )
    },

    canPrintDeposit() {
      return Boolean(
        String(this.form.depositAccountNumber || '').trim() &&
        String(this.form.depositOwner || '').trim() &&
        String(this.form.depositorName || '').trim() &&
        this.normalizedAmount
      )
    },
  },

  created() {
    this.form.date = toJalaali(new Date()).date
  },

  mounted() {
    this.focusDateField()

    document.addEventListener(
      'keydown',
      this.handleKeyboardShortcuts,
      true
    )
  },

  beforeDestroy() {
    document.removeEventListener(
      'keydown',
      this.handleKeyboardShortcuts,
      true
    )
  },

  methods: {
    goToDashboard() {
      this.$router.back()
    },

    focusWithdrawalAccount() {
      this.$nextTick(() => {
        const component = this.$refs.withdrawalAccountField

        if (
          component &&
          component.$refs &&
          component.$refs.list
        ) {
          component.$refs.list.focus()
        }
      })
    },

    onWithdrawalAccountSelected(account) {
      this.withdrawalAccount = account ? {
        ...account
      } : {}

      if (account) {
        if (account.branch) {
          this.form.branch = account.branch
        }

        if (account.national_code) {
          this.form.nationalCode = account.national_code
        }
      }
    },

    printWithdrawal() {
      if (!this.validateCommonFields()) {
        return
      }

      if (!this.form.withdrawalAccountId) {
        this.$refs.form.validate()
        return
      }

      if (!this.normalizedAmount) {
        this.$refs.form.validate()
        return
      }

      const fields = {
        ...this.getCommonFields(),

        amount: `${this.amountFormatted} ریال`,
        amountInWords: `${this.amountInWords} ریال`,

        accountNumber: toPersianDigits(
          this.withdrawalAccount.number || ''
        ),

        owner: this.withdrawalAccount.owner || '',
      }

      this.openPrintWindow(
        'چاپ برداشت ملت',
        fields, {
          ...COMMON_LAYOUT,
          ...WITHDRAWAL_LAYOUT,
        }
      )
    },

    printDeposit() {
      if (!this.validateCommonFields()) {
        return
      }

      if (!this.canPrintDeposit) {
        this.$refs.form.validate()
        return
      }

      const fields = {
        ...this.getCommonFields(),

        amount: `${this.amountFormatted} ریال`,
        amountInWords: `${this.amountInWords} ریال`,

        accountNumber: toPersianDigits(
          this.form.depositAccountNumber
        ),

        owner: this.form.depositOwner,

        depositorName: this.form.depositorName,

        reason: this.form.depositReason,
      }

      this.openPrintWindow(
        'چاپ واریز ملت',
        fields, {
          ...COMMON_LAYOUT,
          ...DEPOSIT_LAYOUT,
        }
      )
    },

    validateCommonFields() {
      const date = String(this.form.date || '').trim()
      const branch = String(this.form.branch || '').trim()

      if (!date || !branch || !this.normalizedAmount) {
        this.$refs.form.validate()
        return false
      }

      return true
    },

    getCommonFields() {
      return {
        date: toPersianDigits(this.form.date),
        branch: toPersianDigits(this.form.branch),
        nationalCode: toPersianDigits(this.form.nationalCode),
        address: this.form.address,
        phone: toPersianDigits(this.form.phone),
      }
    },

    openPrintWindow(title, fields, layout) {
      const printWindow = window.open('', '_blank')

      if (!printWindow) {
        alert(
          'پنجره چاپ باز نشد. لطفاً اجازه باز شدن پنجره جدید را بدهید.'
        )

        return
      }

      const fieldsHtml = Object.keys(layout)
        .map(key => {
          const value = fields[key]

          if (
            value === null ||
            value === undefined ||
            value === ''
          ) {
            return ''
          }

          const config = layout[key]

          return `
            <div
              class="print-field"
              style="
                left: ${config.left}mm;
                top: ${config.top}mm;
                width: ${config.width}mm;
                font-size: ${config.fontSize}pt;
                text-align: ${config.align};
              "
            >${escapeHtml(value)}</div>
          `
        })
        .join('')

      const html = `
        <!DOCTYPE html>
        <html lang="fa" dir="rtl">
        <head>
          <meta charset="UTF-8">
          <title>${escapeHtml(title)}</title>

          <style>
          @page {
              size: A4 portrait;
              margin: 0;
            }

            * {
              box-sizing: border-box;
            }

            html,
            body {
              margin: 0;
              padding: 0;
              width: 219mm;
              height: 138mm;
              direction: rtl;
            }

            body {
              font-family: IRANSans, Tahoma, sans-serif;
              color: #000;
            }

            .print-sheet {
              position: relative;
              width: 219mm;
              height: 138mm;
              overflow: hidden;
              background: #fff;
            }

            .print-field {
              position: absolute;
              margin: 0;
              padding: 0;
              line-height: 1.4;
              white-space: nowrap;
              color: #000;
              direction: rtl;
              unicode-bidi: plaintext;
            }

            @media screen {
              body {
                background: #eee;
              }

              .print-sheet {
                margin: 10px auto;
                box-shadow: 0 0 5px #999;
              }
            }

            @media print {
              html,
              body {
                width: 219mm;
                height: 138mm;
              }

              .print-sheet {
                margin: 0;
                box-shadow: none;
              }
            }
          </style>
        </head>

        <body>
          <div class="print-sheet">
            ${fieldsHtml}
          </div>

          <script>
            window.onload = function () {
              window.focus();

              setTimeout(function () {
                window.print();
              }, 300);
            };
          ${'<' + '/script>'}
        </body>
        </html>
      `

      printWindow.document.open()
      printWindow.document.write(html)
      printWindow.document.close()
    },

    resetForm() {
      this.form = {
        date: toJalaali(new Date()).date,
        branch: '',
        nationalCode: '',
        address: DEFAULT_ADDRESS,
        phone: DEFAULT_PHONE,

        amount: '',

        withdrawalAccountId: null,

        depositAccountNumber: '',
        depositOwner: '',
        depositorName: 'قاسمی',
        depositReason: 'خرید نقره',
      }

      this.withdrawalAccount = {}

      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }

        this.focusDateField()
      })
    },

    focusDateField() {
      this.$nextTick(() => {
        const component = this.$refs.dateField

        if (component && component.$el) {
          const input = component.$el.querySelector('input')

          if (input) {
            input.focus()
            return
          }
        }

        if (component && component.$refs) {
          const input = component.$refs.input

          if (input && typeof input.focus === 'function') {
            input.focus()
          }
        }
      })
    },

    handleKeyboardShortcuts(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation()

        this.goToDashboard()
      }
    },
  },
}
</script>

<style scoped>
.bank-internal-page {
  direction: rtl;
  text-align: right;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 18px;
  color: var(--v-primary-base);
}

.section-title .v-icon {
  font-size: 23px;
}

.amount-summary {
  padding: 10px 12px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.035);
  font-size: 16px;
  line-height: 2;
  overflow-wrap: anywhere;
}

.amount-label {
  font-weight: 600;
  margin-left: 6px;
}


@media print {

  html,
  body {
    width: 219mm;
    height: 138mm;
  }

  .print-sheet {
    margin: 0;
    box-shadow: none;
  }
}
</style>
